import {t} from '@lingui/macro'
import {useEffect} from 'react'

import {Modal} from '@pleo-io/telescope'

import {useSubscription, Interval, stripeIntervalToBillingInterval} from '~/api/deimos/billing'
import {useDeimosStorageWithCompanyId} from '~/api/deimos/storage'
import type {PlanUpgradeSource} from '~/api/types/plan'
import {PlanType} from '~/api/types/plan'
import {useTermsDocuments} from '~/country-configuration/features/terms/terms'
import {dayjs} from '~/dates/dayjs'
import {getErrorMessage} from '~/error/message'
import {reportError} from '~/error/report'
import {useHasAWalletLoad} from '~/feature-funds-management/use-has-a-wallet-load'
import useConditionedPlans from '~/feature-ui/plan-pill/use-conditioned-plans'
import {useActionWithFeedback} from '~/generic-ui/use-action-with-feedback/use-action-with-feedback'
import {useToaster} from '~/shell/toaster'
import {getCountry, useUser} from '~/shell/user'
import tracking from '~/tracking'

import type {UpdatePlanProps} from './modal-choose-plan'
import {ChoosePlanModal} from './modal-choose-plan'

import {useEndTrialModalRequirements} from '../end-trial'

interface ChoosePlanContainerProps {
    isOpen: boolean
    onDismiss: (didCompleteAction?: boolean) => void
    planUpgradeSource?: PlanUpgradeSource
    trackingOrigin?: string
}

export const ChoosePlanContainer = ({
    isOpen,
    planUpgradeSource,
    onDismiss,
    trackingOrigin,
}: ChoosePlanContainerProps) => {
    const LARGE_THRESHOLD_DAYS = 5
    const SMALL_THRESHOLD_DAYS = 1

    const user = useUser()
    const {showToast} = useToaster()

    useEffect(() => {
        if (isOpen) {
            tracking.choosePlanModalActioned({
                action: 'viewed',
                origin: trackingOrigin,
            })
        }
    }, [isOpen, trackingOrigin])

    const conditionedPlans = useConditionedPlans()

    const {
        data: subscription,
        mutations: {updateSubscription},
    } = useSubscription(user?.companyId)

    const {
        mutations: {setItem: setPricingPlanInDeimosStorage},
    } = useDeimosStorageWithCompanyId('pricing_plan', user?.companyId)
    const {purchaseTerms} = useTermsDocuments()

    const trialEndDate = subscription?.trial?.end ? new Date(subscription.trial.end) : undefined

    const futureDateClose = new Date()
    futureDateClose.setDate(futureDateClose.getDate() + LARGE_THRESHOLD_DAYS)

    const futureDateSuperClose = new Date()
    futureDateSuperClose.setDate(futureDateSuperClose.getDate() + SMALL_THRESHOLD_DAYS)

    const {hasAWalletLoad} = useHasAWalletLoad()
    const {shouldShowEndTrialModal} = useEndTrialModalRequirements()

    const updatePlan = useActionWithFeedback(
        async ({
            planType,
            interval = Interval.MONTH,
            setLoading,
            essentialPlanPrices,
            advancedPlanPrices,
            contractIntervalCommitment,
        }: UpdatePlanProps) => {
            try {
                switch (planType) {
                    case PlanType.FREE:
                        await updateSubscription({
                            companyId: user?.companyId,
                            planType: PlanType.FREE,
                            startDate: dayjs(trialEndDate || subscription?.started),
                            planUpgradeSource,
                        })
                        break
                    case PlanType.TEAM:
                        await updateSubscription({
                            companyId: user?.companyId,
                            planType: PlanType.TEAM,
                            startDate: dayjs(trialEndDate || subscription?.started),
                            intervalCommitment: 1,
                            userCommitment: 0,
                            planUpgradeSource,
                        })
                        break
                    case PlanType.BUSINESS:
                        await updateSubscription({
                            companyId: user?.companyId,
                            planType: PlanType.BUSINESS,
                            startDate: dayjs(trialEndDate || subscription?.started),
                            intervalCommitment: 1,
                            interval: Interval.YEAR,
                            planUpgradeSource,
                        })
                        break
                    case PlanType.STARTER:
                        await updateSubscription({
                            companyId: user?.companyId,
                            planType: PlanType.STARTER,
                            startDate: dayjs(trialEndDate || subscription?.started),
                            numberOfUsers: user?.company?.numberOfUsers,
                            planUpgradeSource,
                        })
                        break
                    case PlanType.ESSENTIAL:
                        {
                            let clickwrapAgreement

                            if (essentialPlanPrices) {
                                clickwrapAgreement = {
                                    contractPrice: {
                                        currency: essentialPlanPrices?.currency,
                                        value: essentialPlanPrices?.price,
                                    },
                                    discountedContractPrice: {
                                        currency: essentialPlanPrices?.currency,
                                        value: essentialPlanPrices?.discountedPrice,
                                    },
                                    purchaseAgreementUrl: purchaseTerms,
                                    contractIntervalCommitment,
                                }
                            }

                            await updateSubscription({
                                companyId: user?.companyId,
                                planType: PlanType.ESSENTIAL,
                                startDate: dayjs(trialEndDate || subscription?.started),
                                intervalCommitment: 1,
                                intervalType: stripeIntervalToBillingInterval(interval),
                                numberOfUsers: user?.company?.numberOfUsers,
                                includeCashback: contractIntervalCommitment === 12,
                                clickwrapAgreement,
                                planUpgradeSource,
                            })
                        }

                        break
                    case PlanType.ADVANCED:
                        {
                            let clickwrapAgreement

                            if (advancedPlanPrices) {
                                clickwrapAgreement = {
                                    contractPrice: {
                                        currency: advancedPlanPrices?.currency,
                                        value: advancedPlanPrices?.price,
                                    },
                                    discountedContractPrice: {
                                        currency: advancedPlanPrices?.currency,
                                        value: advancedPlanPrices?.discountedPrice,
                                    },
                                    purchaseAgreementUrl: purchaseTerms,
                                    contractIntervalCommitment,
                                }
                            }

                            await updateSubscription({
                                companyId: user?.companyId,
                                planType: PlanType.ADVANCED,
                                startDate: dayjs(trialEndDate || subscription?.started),
                                intervalCommitment: 1,
                                intervalType: stripeIntervalToBillingInterval(interval),
                                numberOfUsers: user?.company?.numberOfUsers,
                                includeCashback: contractIntervalCommitment === 12,
                                clickwrapAgreement,
                                planUpgradeSource,
                            })
                        }
                        break
                    default:
                        await updateSubscription({
                            companyId: user?.companyId,
                            planType: conditionedPlans.essential.type,
                            startDate: dayjs(trialEndDate || subscription?.started),
                            intervalCommitment: 1,
                            userCommitment: 0,
                            planUpgradeSource,
                        })
                        break
                }

                await setPricingPlanInDeimosStorage(planType.toLowerCase())

                tracking.choosePlanModalActioned({
                    action: 'completed',
                    plan: planType,
                    interval,
                    origin: trackingOrigin,
                    commitment: contractIntervalCommitment,
                })
                onDismiss(true)
                return {status: 'success'}
            } catch (err) {
                reportError(err)
                showToast(getErrorMessage(err), {level: 'error'})
                onDismiss()
                return {status: 'cancelled'}
            } finally {
                setLoading(false)
            }
        },
        () => ({
            successMessage: t`Plan changed successfully`,
            errorMessage: t`An error occurred. Please try again later or contact support.`,
        }),
    )

    const getIsLastDay = () => {
        const isLastDay =
            trialEndDate && trialEndDate < futureDateSuperClose && new Date() < trialEndDate

        return Boolean(isLastDay)
    }

    const getIsAlmostOver = () => {
        // is within large threshhold BUT IS NOT the last day
        const isAlmostOver =
            trialEndDate && trialEndDate < futureDateClose && trialEndDate > futureDateSuperClose

        return Boolean(isAlmostOver)
    }

    const getIsDismissible = () => {
        const hasPlan = !!subscription?.planSubscriptionItem?.plan?.type
        if (
            (!trialEndDate || getIsLastDay()) &&
            hasPlan &&
            hasAWalletLoad &&
            shouldShowEndTrialModal
        ) {
            return false
        }

        return true
    }

    const onDismissWithTracking = (didCompleteAction?: boolean) => {
        if (!didCompleteAction) {
            tracking.choosePlanModalActioned({
                action: 'abandoned',
                origin: trackingOrigin,
            })
        }
        onDismiss(didCompleteAction)
    }

    return (
        <Modal
            style={{width: 1066, paddingTop: 0}}
            isOpen={isOpen}
            aria-labelledby="choose-plan-modal-header-text"
        >
            <ChoosePlanModal
                isDismissible={getIsDismissible()}
                trialEndDate={trialEndDate}
                onDismiss={onDismissWithTracking}
                updatePlan={updatePlan}
                isLastDay={getIsLastDay()}
                isAlmostOver={getIsAlmostOver()}
                hasFooter
                subscription={subscription}
                country={getCountry(user)!}
            />
        </Modal>
    )
}
